import { useCallback, useEffect, useMemo, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useMutation } from '@tanstack/react-query'

import { showLoading } from 'services/util'
import css from './sass/adminitem.module.scss'
import themeCss from '../components/sass/theme.module.scss'
import ItemOrderArrangeRow from './ItemOrderArrangeRow'
import { graphql } from '../api'
import { updateAuctionMutation } from '../graphql/Mutations'
import { AUCTION_ITEM, FIXED_PRICE_ITEM, getInitialItemDisplayOrder, getOrderEntryKey, itemDisplayOrdersMatch, sortItemsByPreset } from './itemorderutils'

export default function ItemOrderArrangePanel(props) {
  return (
    <DndProvider backend={HTML5Backend}>
      <ItemOrderArrangePanelContent {...props} />
    </DndProvider>
  )
}

function ItemOrderArrangePanelContent({ auction, refreshAdminAuctionQuery, toAllItemsAndDonations }) {
  const [savedItemDisplayOrder, setSavedItemDisplayOrder] = useState(() => getInitialItemDisplayOrder(auction))
  const [itemDisplayOrder, setItemDisplayOrder] = useState(savedItemDisplayOrder)

  const { error: saveError, isPending: isSaving, mutate: saveItemDisplayOrder } = useMutation({
    mutationFn: (displayOrder) => graphql({
      query: updateAuctionMutation,
      variables: {input: {AuctionId: auction.AuctionId, ItemDisplayOrder: displayOrder}},
    }),
    onSuccess: (_response, savedOrder) => {
      setSavedItemDisplayOrder(savedOrder)
      refreshAdminAuctionQuery()
    },
  })

  const itemLookup = useMemo(() => {
    const lookup = new Map()
    const items = auction?.Items || []
    const fixedItems = auction?.FixedItems || []

    items.forEach((item) => {
      lookup.set(getOrderEntryKey({ ItemType: AUCTION_ITEM, ItemId: item.ItemId }), item)
    })

    fixedItems.forEach((item) => {
      lookup.set(getOrderEntryKey({ ItemType: FIXED_PRICE_ITEM, ItemId: item.FixedItemId }), item)
    })

    return lookup
  }, [auction])

  const sortItems = (preset) => {
    setItemDisplayOrder(sortItemsByPreset(auction, preset))
  }

  const moveItem = useCallback((fromIndex, toIndex) => {
    setItemDisplayOrder((currentOrder) => {
      const updatedOrder = [...currentOrder]
      const [movedItem] = updatedOrder.splice(fromIndex, 1)
      updatedOrder.splice(toIndex, 0, movedItem)
      return updatedOrder
    })
  }, [])

  const hasUnsavedOrder = !itemDisplayOrdersMatch(itemDisplayOrder, savedItemDisplayOrder)

  useEffect(() => {
    showLoading(isSaving)
  }, [isSaving])

  return (
    <div className={css['item-order-arrange-panel']}>
      <div className={css['item-order-arrange-actions']}>
        <button className={css['back-to-items-link']} onClick={toAllItemsAndDonations} >
          ⬅ Back to All Items &amp; Donations
        </button>
        <button
          type='button'
          className={`${css['item-order-save-button']} ${themeCss['button']} ${themeCss['FilledBlue']}`}
          disabled={!hasUnsavedOrder || isSaving}
          onClick={() => saveItemDisplayOrder(itemDisplayOrder)}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {saveError &&
        <p role='alert'>
          Unable to save the item display order. Please try again.
        </p>
      }

      <h2>Arrange Display Order</h2>
      <div className={css['item-order-arrange-instructions']}>
        <p>Drag and drop items to change the order that they will display in the auction page listing for the attendees.</p>
        <p>You can also select from the below options. You must click <b>Save</b> to apply any changes.</p>
        <div className={css['item-order-arrange-preset-buttons']}>
          <button className={`${themeCss['button']} ${themeCss['FilledBlue']}`} onClick={() => sortItems('type')}>
            Alpha by Item Type
          </button>
          <button className={`${themeCss['button']} ${themeCss['FilledBlue']}`} onClick={() => sortItems('category')}>
            Alpha by Category
          </button>
          <button className={`${themeCss['button']} ${themeCss['FilledBlue']}`} onClick={() => sortItems('title')}>
            Alpha by Title
          </button>
        </div>
      </div>

      {itemDisplayOrder.length === 0
        ? <p>There are no items to arrange.</p>
        : <div className={css['item-order-arrange-list-container']}>
            <div className={css['item-order-arrange-heading']} aria-hidden='true'>
              <span></span>
              <span>Image</span>
              <span>Title</span>
              <span>Category</span>
              <span>Item Type</span>
            </div>
            <ul className={css['item-order-arrange-list']}>
              {itemDisplayOrder.map((orderEntry, index) => {
                const item = itemLookup.get(getOrderEntryKey(orderEntry))

                if (!item) return null

                return (
                  <ItemOrderArrangeRow
                    key={getOrderEntryKey(orderEntry)}
                    auctionCode={auction.AuctionCode}
                    index={index}
                    item={item}
                    itemType={orderEntry.ItemType}
                    moveItem={moveItem}
                  />
                )
              })}
            </ul>
          </div>
      }
    </div>
  )
}
